import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Product } from "./types";
import AddProductModal from "../../components/modals/AddProductModal";
import { BsPencil, BsTrash2 } from "react-icons/bs";
import { FiFilter } from "react-icons/fi";
import AddCategoryModal from "../../components/modals/AddCategoryModal";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAllProducts, getAllProductType } from "../../redux/thunk";

export default function StoreDetail() {
  const { storeId } = useParams<{ storeId: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("product-stock");
  const [searchTerm, setSearchTerm] = useState("");
  const productsPerPage = 20;
  const location = useLocation();
  const { token } = useAppSelector((store) => store.auth);

  useEffect(() => {
    const loadStoreAndProducts = async () => {
      setLoading(true);
      try {
        if (storeId) {
          // Try to fetch all products without server-side pagination
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/getProductsByWarehouse?warehouseId=${location.state?.stateId}&storeName=${location.state?.name}&limit=1000`,
            {
              headers: {
                Authorization: token,
              },
            }
          );

          // Handle different response structures
          if (res.data.products && Array.isArray(res.data.products)) {
            setProducts(res.data.products);
          } else if (res.data && Array.isArray(res.data)) {
            setProducts(res.data);
          } else {
            console.warn("Unexpected API response structure:", res.data);
            setProducts([]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch store details:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadStoreAndProducts();
  }, [storeId, location.state?.stateId, location.state?.name, token]);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowAddProductModal(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowAddProductModal(true);
  };

  const handleProductAdded = (newProduct: Product) => {
    if (editingProduct) {
      setProducts(
        products.map((p) => (p._id === newProduct._id ? newProduct : p))
      );
    } else {
      setProducts([...products, newProduct]);
    }
    setShowAddProductModal(false);
  };

  const getAttributeClass = (attribtes: Product["attribtes"]) => {
    switch (attribtes) {
      case "Top Selling":
        return "text-green-600";
      case "Selling":
        return "text-orange-500";
      case "Regular":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  // Search and Filter Logic
  const filteredProducts = products.filter((product) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.type.toLowerCase().includes(searchLower) ||
      product.attribtes.toLowerCase().includes(searchLower) ||
      product.price.toString().includes(searchLower)
    );
  });

  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-lg text-gray-600">Products not found.</p>
        <Link
          to="/dashboard/warehouse-management"
          className="text-green-600 hover:underline mt-4 inline-block"
        >
          Back to Ware House List
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <Link
          to="/dashboard/warehouse-management"
          className="flex items-center text-xl font-medium"
        >
          Ware House
        </Link>
        <span className="mx-2">→</span>
        <p className="text-gray-600">{location.state?.name}</p>
      </div>

      <div className="mb-6">
        <div className="flex border-b">
          <button
            className={`px-6 py-3 font-medium ${
              activeTab === "product-stock"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("product-stock")}
          >
            Product Stock
          </button>
          <button
            className={`px-6 py-3 font-medium ${
              activeTab === "categories"
                ? "text-green-600 border-b-2 border-green-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("categories")}
          >
            Categories
          </button>
        </div>
      </div>

      {activeTab === "product-stock" && (
        <>
          <div className="flex justify-between mb-6">
            <div>
              <input
                type="text"
                placeholder="Search products by name, category, or price..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="px-4 py-2 border rounded-md w-64 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              {searchTerm && (
                <p className="text-sm text-gray-500 mt-1">
                  {filteredProducts.length} of {products.length} products found
                </p>
              )}
            </div>
            <div className="flex space-x-4">
              <button
                onClick={handleAddProduct}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Add Product
              </button>
              {/* <button className="px-4 py-2 border rounded-md flex items-center">
                <FiFilter className="w-4 h-4 mr-2" />
                Filters
              </button> */}
            </div>
          </div>

          <div className="bg-white rounded-md shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Image
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Product Name
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Category
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Price
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Piece
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Attributes
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentProducts.map((product) => (
                    <tr key={product._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img
                            className="h-12 w-12 rounded-md object-cover"
                            src={
                              product.image ||
                              `/placeholder.svg?height=48&width=48`
                            }
                            alt={product.name}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {product.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {product.type}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          N{product.price.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {product.quantity}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm ${getAttributeClass(
                            product.attribtes
                          )}`}
                        >
                          {product.attribtes}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditProduct(product)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded-md hover:bg-gray-100"
                          >
                            <BsPencil className="w-5 h-5" />
                          </button>
                          <button className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-gray-100">
                            <BsTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* No search results */}
          {filteredProducts.length === 0 && searchTerm && (
            <div className="text-center py-8">
              <p className="text-lg text-gray-600">
                No products found for "{searchTerm}"
              </p>
              <button
                onClick={() => setSearchTerm("")}
                className="text-green-600 hover:underline mt-2"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Pagination */}
          {filteredProducts.length > 0 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-500">
                Showing {indexOfFirstProduct + 1} to{" "}
                {Math.min(indexOfLastProduct, filteredProducts.length)} of{" "}
                {filteredProducts.length} entries
                {searchTerm && (
                  <span className="text-gray-400">
                    {" "}
                    (filtered from {products.length} total)
                  </span>
                )}
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-md border disabled:opacity-50 hover:bg-gray-100"
                >
                  &lt;
                </button>

                {/* Show first page */}
                {currentPage > 3 && (
                  <>
                    <button
                      onClick={() => paginate(1)}
                      className="px-3 py-1 rounded-md border hover:bg-gray-100"
                    >
                      1
                    </button>
                    {currentPage > 4 && <span className="px-2 py-1">...</span>}
                  </>
                )}

                {/* Show pages around current page */}
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNumber: number;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else {
                    const start = Math.max(1, currentPage - 2);
                    const end = Math.min(totalPages, start + 4);
                    const adjustedStart = Math.max(1, end - 4);
                    pageNumber = adjustedStart + i;

                    if (pageNumber > totalPages) return null;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === pageNumber
                          ? "bg-green-600 text-white"
                          : "border hover:bg-gray-100"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                {/* Show last page */}
                {currentPage < totalPages - 2 && totalPages > 5 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <span className="px-2 py-1">...</span>
                    )}
                    <button
                      onClick={() => paginate(totalPages)}
                      className={`px-3 py-1 rounded-md border hover:bg-gray-100 ${
                        currentPage === totalPages
                          ? "bg-green-600 text-white"
                          : ""
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-md border disabled:opacity-50 hover:bg-gray-100"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === "categories" && <CategoriesTab storeId={storeId || ""} />}

      {showAddProductModal && (
        <AddProductModal
          storeId={storeId || ""}
          product={editingProduct}
          onClose={() => setShowAddProductModal(false)}
          onProductAdded={handleProductAdded}
          warehouseId={location.state?.stateId}
          storeName={location.state?.name}
          location={location.state?.location}
        />
      )}
    </div>
  );
}

// Categories Tab Component
const CategoriesTab = ({ storeId }: { storeId: string }) => {
  const [loading, setLoading] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const dispatch = useAppDispatch();
  const { categories } = useAppSelector((store) => store.products);

  useEffect(() => {
    setLoading(true);
    Promise.resolve(dispatch(getAllProductType())).then(() => {
      setLoading(false);
    });
  }, [dispatch]);

  const handleAddCategory = () => {
    setShowAddCategoryModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between mb-6">
        <button
          onClick={handleAddCategory}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
        >
          Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category: any) => (
          <div
            key={category._id}
            className="bg-white rounded-md shadow overflow-hidden"
          >
            <div className="h-48 overflow-hidden">
              <img
                src={category.image || `/placeholder.svg?height=192&width=384`}
                alt={category.type}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4 flex items-center justify-between">
              <h3 className="font-medium capitalize text-secondary">
                {category.type}
              </h3>
              <p className="text-gray-600">{category.amountOfProducts || 0}</p>
            </div>
          </div>
        ))}
      </div>

      {showAddCategoryModal && (
        <AddCategoryModal
          onClose={() => setShowAddCategoryModal(false)}
          onCategoryAdded={(category) => {
            setShowAddCategoryModal(false);
          }}
        />
      )}
    </div>
  );
};
